import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  useTheme,
  CircularProgress,
  Paper,
  useMediaQuery,
  Button,
  Chip,
  Rating as MuiRating,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  IconButton,
  alpha
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { exploreApi } from '../services/api';
import { GoogleMapsService } from '../services/googleMaps';
import CafeList from '../components/Explore/CafeList';
import CafeCard from '../components/Explore/CafeCard';
import StatsSection from '../components/Explore/StatsSection';
import { CafeResultsSkeleton } from '../components/skeletons';
import RatingModal from '../components/Explore/RatingModal';
import { Cafe, CafeVisit } from '../types/explore';
import toast from 'react-hot-toast';
import {
  LocationOn,
  LocalCafe,
  Coffee,
  NavigateNext,
  MyLocation,
  Search as SearchIcon,
  FilterList,
  Sort,
  ArrowBack,
  ArrowUpward,
  ArrowDownward,
  PriceCheck,
  Schedule,
  Place,
  Close as CloseIcon,
  Star
} from '@mui/icons-material';
import discoverImage from '../assets/WFHCafe_WObg.png'; 
import { useNavigate, useLocation } from 'react-router-dom';

const ExplorePage: React.FC = () => {
  const [cafes, setCafes] = useState<Cafe[]>([]);
  const [filteredCafes, setFilteredCafes] = useState<Cafe[]>([]);
  const [selectedCafe, setSelectedCafe] = useState<Cafe | null>(null);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [userLocationAddress, setUserLocationAddress] = useState<string>('');
  const [mapCenter, setMapCenter] = useState({ lat: 19.0760, lng: 72.8777 });
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [pendingVisit, setPendingVisit] = useState<CafeVisit | null>(null);
  const [recentlyVisitedCafe, setRecentlyVisitedCafe] = useState<{
    cafeName: string;
    visitedAt: string;
    visitId: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalCafes: 0,
    avgRating: 0,
    topRated: '',
    mostReviewed: 0
  });
  
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('rating_desc');
  const [searchAddress, setSearchAddress] = useState<string>('');
  const [gettingLocation, setGettingLocation] = useState<boolean>(false);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Add this to access query parameters
  
  const { user } = useAuth();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  // Handle query parameter on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    
    if (searchQuery) {
      setSearchAddress(searchQuery);
      // Auto-search after component mounts
      const timer = setTimeout(() => {
        handleLocationSelect(searchQuery);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [location.search]);

  // Filter and sort cafes
  useEffect(() => {
    let result = [...cafes];
    
    if (activeFilters.includes('open_now')) {
      result = result.filter(cafe => cafe.openingHours?.openNow === true);
    }
    if (activeFilters.includes('rating_4plus')) {
      result = result.filter(cafe => (cafe.rating || 0) >= 4);
    }
    if (activeFilters.includes('price_low')) {
      result = result.filter(cafe => (cafe.priceLevel || 0) <= 2);
    }
    
    switch (sortBy) {
      case 'rating_desc':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'rating_asc':
        result.sort((a, b) => (a.rating || 0) - (b.rating || 0));
        break;
      case 'reviews_desc':
        result.sort((a, b) => (b.userRatingsTotal || 0) - (a.userRatingsTotal || 0));
        break;
      case 'reviews_asc':
        result.sort((a, b) => (a.userRatingsTotal || 0) - (b.userRatingsTotal || 0));
        break;
      case 'price_desc':
        result.sort((a, b) => (b.priceLevel || 0) - (a.priceLevel || 0));
        break;
      case 'price_asc':
        result.sort((a, b) => (a.priceLevel || 0) - (b.priceLevel || 0));
        break;
      default:
        break;
    }
    
    setFilteredCafes(result);
    
    if (result.length > 0) {
      const avgRating = result.reduce((sum, cafe) => sum + (cafe.rating || 0), 0) / result.length;
      const topRated = result.reduce((max, cafe) => (cafe.rating || 0) > (max.rating || 0) ? cafe : max);
      const mostReviewed = result.reduce((max, cafe) => (cafe.userRatingsTotal || 0) > (max.userRatingsTotal || 0) ? cafe : max);
      
      setStats({
        totalCafes: result.length,
        avgRating: parseFloat(avgRating.toFixed(1)),
        topRated: topRated.name,
        mostReviewed: mostReviewed.userRatingsTotal || 0
      });
    } else if (cafes.length > 0) {
      const avgRating = cafes.reduce((sum, cafe) => sum + (cafe.rating || 0), 0) / cafes.length;
      const topRated = cafes.reduce((max, cafe) => (cafe.rating || 0) > (max.rating || 0) ? cafe : max);
      const mostReviewed = cafes.reduce((max, cafe) => (cafe.userRatingsTotal || 0) > (max.userRatingsTotal || 0) ? cafe : max);
      
      setStats({
        totalCafes: cafes.length,
        avgRating: parseFloat(avgRating.toFixed(1)),
        topRated: topRated.name,
        mostReviewed: mostReviewed.userRatingsTotal || 0
      });
    }
  }, [cafes, activeFilters, sortBy]);

  const checkPendingRatings = async () => {
    if (!user) return;
    try {
      const response = await exploreApi.getPendingRatings();
      if (response.visits.length > 0) {
        setPendingVisit(response.visits[0]);
        setShowRatingModal(true);
      }
    } catch (error) {
      console.error('Error checking pending ratings:', error);
    }
  };

  const handleLocationSelect = async (address: string) => {
    if (!address.trim()) {
      toast.error('Please enter a location');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await exploreApi.geocodeAddress(address);
      const location = response.location;
      
      setMapCenter(location);
      setActiveFilters([]);
      setSortBy('rating_desc');
      
      // Update URL with search parameter
      navigate(`/explore?search=${encodeURIComponent(address)}`, { replace: true });
      
      const cafesResponse = await exploreApi.searchCafes({
        lat: location.lat,
        lng: location.lng,
        address: location.address
      });
      
      setCafes(cafesResponse.cafes);
      toast.success(`Found ${cafesResponse.cafes.length} cafes nearby`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to find cafes';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUseMyLocation = async () => {
    setGettingLocation(true);
    try {
      const location = await GoogleMapsService.getCurrentLocation();
      setUserLocation(location);
      
      const response = await exploreApi.geocodeAddress(`${location.lat},${location.lng}`);
      const address = response.location.address;
      
      setSearchAddress(address);
      setUserLocationAddress(address);
      toast.success('Location found! Click Search to find cafes.');
    } catch (error: any) {
      console.error('Location error:', error);
      toast.error(error.message || 'Failed to get your location. Please check browser permissions.');
    } finally {
      setGettingLocation(false);
    }
  };

  const handleSearch = () => {
    if (searchAddress.trim()) {
      handleLocationSelect(searchAddress);
    } else if (userLocationAddress) {
      handleLocationSelect(userLocationAddress);
    } else {
      toast.error('Please enter a location or use "My Location"');
    }
  };

  const handleCafeSelect = (cafe: Cafe) => {
    setSelectedCafe(cafe);
  };

  const handleNavigateToCafe = async (cafe: Cafe) => {
    if (!user) {
      toast.error('Please login to save your visit');
      return;
    }

    try {
      // Save visit to backend with photos
      const visitResponse = await exploreApi.saveVisit({
        cafeId: cafe.placeId,
        cafeName: cafe.name,
        location: userLocation || undefined,
        cafePhotos: cafe.photos || []
      });

      // Store the recent visit to show rating modal
      setRecentlyVisitedCafe({
        cafeName: cafe.name,
        visitedAt: visitResponse.visit.visitedAt,
        visitId: visitResponse.visit.id
      });

      // Mark that user is navigating away
      setIsNavigatingAway(true);
      
      // Save to localStorage so we know they navigated away
      localStorage.setItem('lastNavigatedCafe', JSON.stringify({
        cafeName: cafe.name,
        visitId: visitResponse.visit.id,
        visitedAt: visitResponse.visit.visitedAt,
        timestamp: Date.now()
      }));

      // Open Google Maps in new tab
      const mapsUrl = GoogleMapsService.getGoogleMapsUrl(cafe);
      window.open(mapsUrl, '_blank');
      
      toast.success(`Navigating to ${cafe.name}. Your visit has been recorded.`);
      
    } catch (error: any) {
      console.error('Save visit error:', error);
      toast.error('Failed to save visit');
    }
  };

  // Check on page load if user just returned from navigation
  useEffect(() => {
    const checkForReturnedNavigation = () => {
      const lastNav = localStorage.getItem('lastNavigatedCafe');
      if (lastNav && user) {
        const navData = JSON.parse(lastNav);
        const timeSinceNav = Date.now() - navData.timestamp;
        
        // If user returned within 5 minutes, show rating modal
        if (timeSinceNav < 5 * 60 * 1000) {
          setRecentlyVisitedCafe({
            cafeName: navData.cafeName,
            visitedAt: navData.visitedAt,
            visitId: navData.visitId
          });
          setShowRatingModal(true);
          localStorage.removeItem('lastNavigatedCafe');
        } else {
          // Clear old navigation data
          localStorage.removeItem('lastNavigatedCafe');
        }
      }
    };

    checkForReturnedNavigation();
  }, [user]);

  // Listen for page visibility changes (user returning to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isNavigatingAway) {
        // User returned to the tab, show rating modal
        setTimeout(() => {
          if (recentlyVisitedCafe) {
            setShowRatingModal(true);
          }
        }, 1000);
        setIsNavigatingAway(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isNavigatingAway, recentlyVisitedCafe]);

  const handleRatingSubmit = async (rating: number, feedback: string) => {
    if (!recentlyVisitedCafe) return;
    
    console.log('📝 Submitting rating for visit:', recentlyVisitedCafe.visitId, { rating, feedback });
    
    try {
      const result = await exploreApi.submitRating(recentlyVisitedCafe.visitId, { rating, feedback });
      console.log('✅ Rating submitted successfully:', result);
      toast.success('Thank you for your feedback!');
      setShowRatingModal(false);
      setRecentlyVisitedCafe(null);
    } catch (error: any) {
      console.error('❌ Failed to save rating:', error.response?.data || error.message);
      toast.error(error.response?.data?.error || 'Failed to save rating');
    }
  };

  // Filter & Sort Handlers
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);
  const handleFilterSelect = (filter: string) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters(activeFilters.filter(f => f !== filter));
    } else {
      setActiveFilters([...activeFilters, filter]);
    }
  };
  const handleClearFilters = () => {
    setActiveFilters([]);
    handleFilterClose();
  };
  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);
  const handleSortSelect = (sort: string) => {
    setSortBy(sort);
    handleSortClose();
  };

  // Map styling
  const mapContainerStyle = {
    width: '100%', 
    height: '500px', 
    borderRadius: '24px',
    overflow: 'hidden',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)'
  };

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: isDark ? '#000000' : '#FAFAFA',
        background: isDark 
          ? '#000000'
          : 'radial-gradient(circle at 50% 0%, rgba(196, 182, 111, 0.1) 0%, #FAFAFA 100%)'
      }}>
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')} 
            sx={{ mb: 3, color: goldColor, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' } }}
          >
            Back to Home
          </Button>
          
          {/* Hero Section */}
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              mb: 2, 
              p: 2, 
              borderRadius: '20px', 
              background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(5, 24, 20, 0.05)',
              backdropFilter: 'blur(20px)',
              border: isDark 
                ? '1px solid rgba(196, 182, 111, 0.2)' 
                : '1px solid rgba(5, 24, 20, 0.1)',
            }}>
              <img src={discoverImage} alt="Discover" style={{ width: 40, height: 40, marginRight: 16 }} />
              <Typography variant="h3" sx={{ 
                ...discordFont,
                background: isDark 
                  ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' 
                  : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                fontSize: { xs: '2rem', md: '3rem' }
              }}>
                Discover Cafes
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)',
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}>
              Find the perfect coffee spot near you. Explore ratings, reviews, and save your favorite visits.
            </Typography>

            {/* Stats Section */}
            {cafes.length > 0 && (
              <StatsSection stats={stats} darkMode={isDark} />
            )}
          </Box>

          {/* Search Section */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              mb: 6, 
              borderRadius: '32px',
              ...glassEffect
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3,
                alignItems: { xs: 'stretch', sm: 'center' },
              }}
            >
              {/* Search Input */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter city, address, or zip code"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                disabled={loading || gettingLocation}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: { sm: 56 },
                    borderRadius: '16px',
                    background: isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(5, 24, 20, 0.05)',
                    '& fieldset': {
                      borderColor: isDark
                        ? 'rgba(196, 182, 111, 0.3)'
                        : 'rgba(5, 24, 20, 0.2)',
                    },
                    '&:hover fieldset': { borderColor: goldColor },
                    '&.Mui-focused fieldset': {
                      borderColor: goldColor,
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: isDark ? '#FFFFFF' : deepGreen,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Place sx={{ color: goldColor, opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Buttons Wrapper */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  justifyContent: 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                {/* Search Button */}
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={
                    (!searchAddress.trim() && !userLocationAddress) ||
                    loading ||
                    gettingLocation
                  }
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{
                    height: { sm: 56 },
                    borderRadius: '16px',
                    background: isDark
                      ? 'rgba(196, 182, 111, 0.9)'
                      : '#051814',
                    color: isDark ? '#051814' : '#FFFFFF',
                    minWidth: { xs: 48, sm: 140 },
                    px: { xs: 1.5, sm: 3 },
                    fontWeight: 700,
                    fontSize: '1rem',
                    '&:hover': {
                      background: isDark
                        ? 'rgba(196, 182, 111, 1)'
                        : '#0F3B2E',
                    },
                  }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>

                {/* Use Location Button */}
                <Button
                  variant="outlined"
                  onClick={handleUseMyLocation}
                  disabled={loading || gettingLocation}
                  startIcon={gettingLocation ? <CircularProgress size={20} /> : <MyLocation />}
                  sx={{
                    height: { sm: 56 },
                    borderRadius: '16px',
                    borderColor: goldColor,
                    color: goldColor,
                    fontWeight: 700,
                    fontSize: '1rem',
                    minWidth: { xs: 48, sm: 180 },
                    px: { xs: 1.5, sm: 3 },
                    '&:hover': {
                      borderColor: goldColor,
                      backgroundColor: isDark
                        ? 'rgba(196, 182, 111, 0.1)'
                        : 'rgba(196, 182, 111, 0.05)',
                    },
                  }}
                >
                  {gettingLocation ? 'Getting Location...' : 'Use My Location'}
                </Button>
              </Box>
            </Box>

            {/* Filters and Sort Buttons */}
            {activeFilters.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mr: 1 }}>
                  Active filters:
                </Typography>
                {activeFilters.map(filter => (
                  <Chip
                    key={filter}
                    label={filter === 'open_now' ? 'Open Now' : filter === 'rating_4plus' ? 'Rating 4+' : 'Budget Friendly'}
                    size="small"
                    onDelete={() => handleFilterSelect(filter)}
                    sx={{
                      background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                      color: goldColor,
                      fontWeight: 600,
                      border: 'none'
                    }}
                  />
                ))}
                <Button size="small" onClick={handleClearFilters} sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', ml: 1 }}>
                  Clear All
                </Button>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
               <Button
                startIcon={<FilterList />}
                onClick={handleFilterClick}
                sx={{
                  background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                  color: goldColor,
                  borderRadius: '16px',
                  fontWeight: 600,
                  px: 3,
                  border: isDark ? '1px solid rgba(196, 182, 111, 0.3)' : '1px solid rgba(196, 182, 111, 0.2)',
                  '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)' }
                }}
              >
                Filters {activeFilters.length > 0 ? `(${activeFilters.length})` : ''}
              </Button>
              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleFilterClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: '16px',
                    background: isDark ? 'rgba(0, 0, 0, 0.9)' : '#fff',
                    backdropFilter: 'blur(20px)',
                    border: isDark ? '1px solid rgba(196, 182, 111, 0.2)' : '1px solid rgba(196, 182, 111, 0.1)',
                    minWidth: 200
                  }
                }}
              >
                <MenuItem onClick={() => handleFilterSelect('open_now')}>
                   <ListItemIcon><Schedule sx={{ color: activeFilters.includes('open_now') ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'inherit') }} /></ListItemIcon>
                   <ListItemText primary="Open Now" primaryTypographyProps={{ color: activeFilters.includes('open_now') ? goldColor : (isDark ? '#FFFFFF' : 'inherit') }} />
                </MenuItem>
                <MenuItem onClick={() => handleFilterSelect('rating_4plus')}>
                   <ListItemIcon><Star sx={{ color: activeFilters.includes('rating_4plus') ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'inherit') }} /></ListItemIcon>
                   <ListItemText primary="Rating 4+" primaryTypographyProps={{ color: activeFilters.includes('rating_4plus') ? goldColor : (isDark ? '#FFFFFF' : 'inherit') }} />
                </MenuItem>
                <MenuItem onClick={() => handleFilterSelect('price_low')}>
                   <ListItemIcon><PriceCheck sx={{ color: activeFilters.includes('price_low') ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'inherit') }} /></ListItemIcon>
                   <ListItemText primary="Budget Friendly" primaryTypographyProps={{ color: activeFilters.includes('price_low') ? goldColor : (isDark ? '#FFFFFF' : 'inherit') }} />
                </MenuItem>
                <Divider sx={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }} />
                <MenuItem onClick={handleClearFilters}>
                  <ListItemText primary="Clear All Filters" primaryTypographyProps={{ color: isDark ? '#FFFFFF' : 'inherit' }} />
                </MenuItem>
              </Menu>

              <Button
                startIcon={<Sort />}
                onClick={handleSortClick}
                sx={{
                  background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                  color: goldColor,
                  borderRadius: '16px',
                  fontWeight: 600,
                  px: 3,
                  border: isDark ? '1px solid rgba(196, 182, 111, 0.3)' : '1px solid rgba(196, 182, 111, 0.2)',
                  '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)' }
                }}
              >
                Sort By
              </Button>
              <Menu
                anchorEl={sortAnchorEl}
                open={Boolean(sortAnchorEl)}
                onClose={handleSortClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: '16px',
                    background: isDark ? 'rgba(0, 0, 0, 0.9)' : '#fff',
                    backdropFilter: 'blur(20px)',
                    border: isDark ? '1px solid rgba(196, 182, 111, 0.2)' : '1px solid rgba(196, 182, 111, 0.1)',
                    minWidth: 200
                  }
                }}
              >
                <MenuItem onClick={() => handleSortSelect('rating_desc')}>
                   <ListItemIcon><ArrowDownward sx={{ color: sortBy === 'rating_desc' ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'inherit') }} /></ListItemIcon>
                   <ListItemText primary="Rating: High to Low" primaryTypographyProps={{ color: sortBy === 'rating_desc' ? goldColor : (isDark ? '#FFFFFF' : 'inherit') }} />
                </MenuItem>
                <MenuItem onClick={() => handleSortSelect('rating_asc')}>
                   <ListItemIcon><ArrowUpward sx={{ color: sortBy === 'rating_asc' ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'inherit') }} /></ListItemIcon>
                   <ListItemText primary="Rating: Low to High" primaryTypographyProps={{ color: sortBy === 'rating_asc' ? goldColor : (isDark ? '#FFFFFF' : 'inherit') }} />
                </MenuItem>
                <MenuItem onClick={() => handleSortSelect('reviews_desc')}>
                   <ListItemIcon><ArrowDownward sx={{ color: sortBy === 'reviews_desc' ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'inherit') }} /></ListItemIcon>
                   <ListItemText primary="Most Reviews" primaryTypographyProps={{ color: sortBy === 'reviews_desc' ? goldColor : (isDark ? '#FFFFFF' : 'inherit') }} />
                </MenuItem>
              </Menu>
            </Box>
          </Paper>

          {/* Main Content - Map and List with Flexbox Layout */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4 }}>
            {/* Map Section - Fixed 900px width on desktop */}
            <Box 
              sx={{ 
                p: 3, 
                borderRadius: '32px',
                ...glassEffect,
                width: { xs: '100%', lg: '900px' },
                height: '100%',
                flexShrink: 0
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, fontSize: '1.5rem' }}>
                  Interactive Map
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mr: 1 }}>
                    Showing:
                  </Typography>
                  <Chip
                    label={`${filteredCafes.length} cafes`}
                    size="small"
                    sx={{
                      background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                      color: goldColor,
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ position: 'relative' }}>
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                      zIndex: 1000,
                      borderRadius: '20px',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress sx={{ color: goldColor, mb: 2 }} size={60} />
                      <Typography sx={{ color: isDark ? '#FFFFFF' : deepGreen, fontWeight: 600, fontSize: '1.2rem' }}>
                        Searching for cafes...
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                <Box sx={mapContainerStyle}>
                  <Map
                    center={mapCenter}
                    zoom={14}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    mapTypeId="roadmap"
                    style={{ width: '100%', height: '100%' }}
                  >
                    {filteredCafes.map((cafe) => (
                      <Marker
                        key={cafe.placeId}
                        position={cafe.location}
                        onClick={() => handleCafeSelect(cafe)}
                      />
                    ))}
                  </Map>
                </Box>
              </Box>
            </Box>
            
            {/* Cafe List Section - Fills remaining space */}
            <Box sx={{ flexGrow: 1, minWidth: '300px' }}>
              <CafeList
                cafes={filteredCafes}
                selectedCafe={selectedCafe}
                onCafeSelect={handleCafeSelect}
                onNavigate={handleNavigateToCafe}
                loading={loading}
                darkMode={isDark}
              />
            </Box>
          </Box>

          {/* Call to Action Section */}
          {!user && cafes.length > 0 && (
            <Box 
              sx={{ 
                mt: 8, 
                p: { xs: 4, md: 6 },
                borderRadius: '32px',
                ...glassEffect,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Coffee sx={{ fontSize: 60, color: goldColor, mb: 3 }} />
              <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
                Save Your Coffee Journey
              </Typography>
              <Typography variant="h6" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', mb: 4, maxWidth: 600, mx: 'auto', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Sign in to track visited cafes, rate your experiences, and get personalized recommendations.
              </Typography>
              <Button
                variant="contained"
                href="/login"
                endIcon={<NavigateNext />}
                sx={{
                  background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814',
                  color: isDark ? '#051814' : '#FFFFFF',
                  fontWeight: 800,
                  borderRadius: '100px',
                  px: 6, py: 2,
                  fontSize: '1.1rem',
                  boxShadow: isDark ? '0 0 30px rgba(196, 182, 111, 0.4)' : '0 0 30px rgba(5, 24, 20, 0.3)',
                  '&:hover': {
                    background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E',
                    transform: 'translateY(-2px)',
                    boxShadow: isDark ? '0 0 40px rgba(196, 182, 111, 0.6)' : '0 0 40px rgba(5, 24, 20, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Sign In to Continue
              </Button>
            </Box>
          )}

          {/* While a search is running there are no results to show yet, so
              stand in with cards of the same shape rather than empty space. */}
          {loading && filteredCafes.length === 0 && <CafeResultsSkeleton />}

          {/* Featured Cafes Section */}
          {cafes.length > 0 && filteredCafes.length > 0 && (
            <Box sx={{ mt: 8, width: '100%' }}>
              <Typography variant="h4" sx={{ 
                ...discordFont,
                color: isDark ? '#FFFFFF' : deepGreen,
                mb: 4,
                textAlign: 'center',
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}>
                Featured Cafes
              </Typography>
              
              <Grid container spacing={3} direction="column">
                {filteredCafes.slice(0, 3).map((featuredCafe) => (
                  <Grid size={{xs: 12}} key={featuredCafe.placeId}>
                    <Box 
                      sx={{ 
                        p: 3, 
                        width: '100%',
                        borderRadius: '32px',
                        ...glassEffect,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'center',
                        gap: 3,
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-5px)' }
                      }}
                    >
                      {/* Image Section */}
                      <Box sx={{ 
                        width: { xs: '100%', md: 200 }, 
                        height: { xs: 180, md: 200 },
                        borderRadius: '20px',
                        overflow: 'hidden',
                        background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: isDark ? '1px solid rgba(196, 182, 111, 0.3)' : '1px solid rgba(196, 182, 111, 0.2)',
                        position: 'relative'
                      }}>
                        {featuredCafe.photos && featuredCafe.photos.length > 0 ? (
                          <Box
                            component="img"
                            src={featuredCafe.photos[0]}
                            alt={featuredCafe.name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.05)'
                              }
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <LocalCafe sx={{ 
                            fontSize: 80, 
                            color: goldColor, 
                            opacity: 0.8 
                          }} />
                        )}
                        
                        {/* Rating badge overlay */}
                        {featuredCafe.rating && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: '#fff',
                              borderRadius: '12px',
                              px: 1.5,
                              py: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backdropFilter: 'blur(4px)',
                              border: `1px solid ${goldColor}`
                            }}
                          >
                            <Star sx={{ fontSize: 14, color: goldColor }} />
                            <Typography variant="caption" sx={{ 
                              fontWeight: 700,
                              fontSize: '0.8rem'
                            }}>
                              {featuredCafe.rating.toFixed(1)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      <Box sx={{ flex: 1, width: '100%' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          mb: 2 
                        }}>
                          <Typography variant="h5" sx={{ 
                            ...discordFont, 
                            color: isDark ? '#FFFFFF' : deepGreen, 
                            mb: 1,
                            flex: 1
                          }}>
                            {featuredCafe.name}
                          </Typography>
                          
                          {/* Photo count badge */}
                          {featuredCafe.photos && featuredCafe.photos.length > 1 && (
                            <Chip
                              icon={<LocalCafe sx={{ fontSize: 14 }} />}
                              label={`${featuredCafe.photos.length} photos`}
                              size="small"
                              sx={{
                                background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                                color: goldColor,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                          <LocationOn sx={{ color: goldColor, fontSize: 20, mt: 0.5 }} />
                          <Typography variant="body2" sx={{ 
                            color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
                            flex: 1 
                          }}>
                            {featuredCafe.address}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                          {featuredCafe.rating && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <MuiRating 
                                value={featuredCafe.rating} 
                                readOnly 
                                precision={0.5} 
                                size="small" 
                                sx={{ color: goldColor }} 
                              />
                              <Typography variant="body2" sx={{ 
                                color: goldColor, 
                                fontWeight: 700,
                                fontSize: '0.9rem'
                              }}>
                                {featuredCafe.rating.toFixed(1)}
                              </Typography>
                              {featuredCafe.userRatingsTotal && (
                                <Typography variant="caption" sx={{ 
                                  color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(5, 24, 20, 0.6)'
                                }}>
                                  ({featuredCafe.userRatingsTotal} reviews)
                                </Typography>
                              )}
                            </Box>
                          )}
                          
                          {featuredCafe.priceLevel && (
                            <Chip
                              label={'$'.repeat(featuredCafe.priceLevel)}
                              size="small"
                              sx={{ 
                                color: goldColor, 
                                borderColor: goldColor,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                height: 24,
                                background: alpha(goldColor, 0.1)
                              }}
                            />
                          )}
                          
                          {featuredCafe.openingHours?.openNow !== undefined && (
                            <Chip
                              icon={<Schedule sx={{ fontSize: 14 }} />}
                              label={featuredCafe.openingHours.openNow ? 'Open Now' : 'Closed'}
                              size="small"
                              color={featuredCafe.openingHours.openNow ? 'success' : 'error'}
                              variant="filled"
                              sx={{ 
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                background: featuredCafe.openingHours.openNow 
                                  ? 'linear-gradient(135deg, #23A559 0%, #0F3B2E 100%)'
                                  : 'linear-gradient(135deg, #DA373C 0%, #0F3B2E 100%)',
                                color: '#fff'
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* Photo preview thumbnails for multiple images */}
                        {featuredCafe.photos && featuredCafe.photos.length > 1 && (
                          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            {featuredCafe.photos.slice(0, 3).map((photo, index) => (
                              <Box
                                key={index}
                                component="img"
                                src={photo}
                                alt={`${featuredCafe.name} photo ${index + 1}`}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    borderColor: goldColor
                                  }
                                }}
                                onClick={() => handleCafeSelect(featuredCafe)}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            ))}
                            {featuredCafe.photos.length > 3 && (
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: '8px',
                                  background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: `1px dashed ${alpha(goldColor, 0.3)}`,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                                    borderColor: goldColor
                                  }
                                }}
                                onClick={() => handleCafeSelect(featuredCafe)}
                              >
                                <Typography variant="caption" sx={{ 
                                  color: goldColor, 
                                  fontWeight: 700 
                                }}>
                                  +{featuredCafe.photos.length - 3}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Button
                            variant="contained"
                            onClick={() => handleNavigateToCafe(featuredCafe)}
                            startIcon={<MyLocation />}
                            sx={{
                              background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814',
                              color: isDark ? '#051814' : '#FFFFFF',
                              fontWeight: 700,
                              borderRadius: '16px',
                              px: 4,
                              py: 1,
                              width: { xs: '100%', md: 'auto' },
                              '&:hover': { 
                                background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E',
                                transform: 'translateY(-2px)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            Visit Cafe
                          </Button>
                          
                          <Button
                            variant="outlined"
                            onClick={() => handleCafeSelect(featuredCafe)}
                            sx={{
                              borderRadius: '16px',
                              borderColor: goldColor,
                              color: goldColor,
                              fontWeight: 600,
                              px: 3,
                              py: 1,
                              '&:hover': {
                                borderColor: goldColor,
                                backgroundColor: alpha(goldColor, 0.1)
                              }
                            }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Cafe Selection Modal */}
          <Dialog
            open={!!selectedCafe}
            onClose={() => setSelectedCafe(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '24px',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
                m: 2
              }
            }}
          >
            {selectedCafe && (
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={() => setSelectedCafe(null)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#fff',
                    zIndex: 10,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)',
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <DialogContent sx={{ p: 0 }}>
                   <CafeCard 
                      cafe={selectedCafe} 
                      onNavigate={() => handleNavigateToCafe(selectedCafe)}
                      darkMode={isDark}
                    />
                </DialogContent>
              </Box>
            )}
          </Dialog>

          {/* Rating Modal */}
          <RatingModal
            open={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            onSubmit={handleRatingSubmit}
            cafeName={recentlyVisitedCafe?.cafeName || ''}
            visitedAt={recentlyVisitedCafe?.visitedAt || ''}
            userName={user?.username || 'User'}
            darkMode={isDark}
            visitId={recentlyVisitedCafe?.visitId || ''} 
          />
        </Container>
      </Box>
    </APIProvider>
  );
};

export default ExplorePage;